import React, { useEffect, useMemo, useState } from 'react';
import { Siren, Send, Loader2, CheckCircle2, Clock, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { AlertTemplates } from '../components/urgent/AlertTemplates';

const fmt = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{children}</button>
);

const Compose = ({ emps, onSent }) => {
  const [f, setF] = useState({ title: '', message: '', audience: 'all', departments: [], employee_ids: [], gender: '' });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const depts = useMemo(() => [...new Set(emps.map((e) => e.department).filter(Boolean))].sort(), [emps]);
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  const matches = emps.filter((e) => q && `${e.name} ${e.employee_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);

  const send = async () => {
    if (!window.confirm('Send this urgent alert now? Staff will see a full-screen pop-up with a repeating chime.')) return;
    setBusy(true);
    try {
      const r = await api.post('/api/urgent-alerts', { ...f, gender: f.gender || null });
      toast({ title: 'Urgent alert sent', description: `Delivered to ${r.data.total} staff` });
      setF({ title: '', message: '', audience: 'all', departments: [], employee_ids: [], gender: '' });
      onSent();
    } catch (e) { toast({ title: 'Could not send', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="urgent-compose-card">
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Siren className="h-5 w-5 text-red-600" /> Send an urgent alert</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <AlertTemplates onUse={(t) => setF((p) => ({ ...p, title: t.title, message: t.message }))} onQuickSent={onSent} />
        <Input placeholder="Title, e.g. Fire drill at 3 PM" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="urgent-title-input" />
        <Textarea placeholder="Message" rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="urgent-message-input" />
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600">Send to</p>
          <div className="flex flex-wrap gap-2">
            {[['all', 'Everyone'], ['departments', 'Departments'], ['employees', 'Chosen staff']].map(([v, l]) => (
              <Chip key={v} on={f.audience === v} onClick={() => setF({ ...f, audience: v })} testid={`urgent-audience-${v}`}>{l}</Chip>
            ))}
          </div>
          {f.audience === 'departments' && (
            <div className="flex flex-wrap gap-1.5" data-testid="urgent-dept-list">
              {depts.map((d) => <Chip key={d} on={f.departments.includes(d)} onClick={() => toggle('departments', d)} testid={`urgent-dept-${d}`}>{d}</Chip>)}
            </div>
          )}
          {f.audience === 'employees' && (
            <div className="space-y-1.5">
              <div className="flex flex-wrap gap-1.5">
                {f.employee_ids.map((id) => { const e = emps.find((x) => x.id === id); return (
                  <span key={id} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">{e?.name}
                    <button onClick={() => toggle('employee_ids', id)}><X className="h-3 w-3" /></button></span>); })}
              </div>
              <Input placeholder="Search staff by name or code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="urgent-staff-search" />
              {matches.map((e) => (
                <button key={e.id} type="button" onClick={() => { toggle('employee_ids', e.id); setQ(''); }} className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-50" data-testid={`urgent-staff-option-${e.employee_code}`}>
                  {e.name} <span className="text-gray-400">{e.employee_code} · {e.department}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs font-semibold text-gray-600">Gender</span>
            {[['', 'Male & Female'], ['Male', 'Male only'], ['Female', 'Female only']].map(([v, l]) => (
              <Chip key={l} on={f.gender === v} onClick={() => setF({ ...f, gender: v })} testid={`urgent-gender-${v || 'all'}`}>{l}</Chip>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={send} disabled={busy || f.title.trim().length < 2 || f.message.trim().length < 2} className="bg-red-600 hover:bg-red-700" data-testid="urgent-send-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send urgent alert
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Detail = ({ id, onClose, onChanged }) => {
  const [a, setA] = useState(null);
  useEffect(() => { if (id) api.get(`/api/urgent-alerts/${id}`).then((r) => setA(r.data)); }, [id]);
  const close = async () => { await api.post(`/api/urgent-alerts/${id}/close`); toast({ title: 'Alert closed' }); onChanged(); onClose(); };
  const done = a?.recipients.filter((r) => r.acked_at) || [];
  const wait = a?.recipients.filter((r) => !r.acked_at) || [];
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="urgent-detail-dialog">
        <DialogHeader><DialogTitle>{a?.title}</DialogTitle></DialogHeader>
        {a && (
          <div className="space-y-4">
            <p className="whitespace-pre-line text-sm text-gray-700">{a.message}</p>
            <p className="text-xs text-gray-500">{a.audience_label} · sent {fmt(a.created_at)} by {a.created_by} · <b>{a.acked} / {a.total} confirmed</b></p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div data-testid="urgent-acked-list">
                <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Confirmed ({done.length})</p>
                {done.map((r) => <p key={r.employee_id} className="text-xs text-gray-700">{r.name} <span className="text-gray-400">{r.employee_code} · {fmt(r.acked_at)}</span></p>)}
              </div>
              <div data-testid="urgent-pending-list">
                <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" /> Not yet ({wait.length})</p>
                {wait.map((r) => <p key={r.employee_id} className="text-xs text-gray-700">{r.name} <span className="text-gray-400">{r.employee_code} · {r.department}</span></p>)}
              </div>
            </div>
            {a.active && <div className="flex justify-end"><Button variant="outline" onClick={close} data-testid="urgent-close-btn">Stop showing this alert</Button></div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function UrgentAlerts() {
  const [emps, setEmps] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get('/api/urgent-alerts').then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { api.get('/api/employees').then((r) => setEmps(r.data || [])).catch(() => {}); load(); }, []);

  return (
    <div className="space-y-6" data-testid="urgent-alerts-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Urgent Alerts</h1>
        <p className="text-sm text-gray-500">Full-screen pop-up in the Employee Portal with a repeating chime until staff tap OK, plus a phone notification.</p>
      </div>
      <Compose emps={emps} onSent={load} />
      <Card>
        <CardHeader><CardTitle className="text-base">Sent alerts</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {list.length === 0 && <p className="text-sm text-gray-400">No urgent alerts sent yet.</p>}
          {list.map((a) => (
            <button key={a.id} onClick={() => setOpen(a.id)} className="flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left hover:bg-gray-50" data-testid={`urgent-row-${a.id}`}>
              <Siren className={`h-4 w-4 ${a.active ? 'text-red-600' : 'text-gray-300'}`} />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium">{a.title}</p>
                <p className="text-[11px] text-gray-500">{a.audience_label} · {fmt(a.created_at)}{a.active ? '' : ' · closed'}</p>
              </div>
              <span className={`text-xs font-semibold ${a.acked === a.total ? 'text-emerald-600' : 'text-amber-600'}`}>{a.acked}/{a.total} confirmed</span>
            </button>
          ))}
        </CardContent>
      </Card>
      <Detail id={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
